import React, { useCallback, useEffect, useMemo, useState } from "react"
import Button from "@mui/material/Button"
import FormGroup from "@mui/material/FormGroup"
import Checkbox from "@mui/material/Checkbox"
import FormLabel from "@mui/material/FormLabel"
import FormControlLabel from "@mui/material/FormControlLabel"
import Alert from "@mui/material/Alert"
import PageHeading from "components/common/LayoutComponents/PageHeading"
import PageContent from "components/common/LayoutComponents/PageContent"
import LoadingScreen from "components/common/LoadingScreen"
import { verifyResponse } from "utils/httpRequest"
import { addSystemConfigs, editSystemConfigs, getSystemConfigByKey } from "services/systemConfig"
import { PeriodicReportSettingsWrapper } from "./PeriodicReportSettings.style"
import { useSnackbar } from "notistack"

const CONFIG_KEY = "periodic_report_settings"
const BASE_WINDOWS = [
  { id: "threeDay", label: "3-day preview" },
  { id: "weekly", label: "Weekly" },
  { id: "monthly", label: "Monthly" },
  { id: "quarterly", label: "Quarterly" },
  { id: "yearly", label: "Yearly" },
]

const baseConfigValue = {
  enabled: true,
  audience: "beta",
  windows: BASE_WINDOWS.map((w) => w.id),
}

export default function PeriodicReportSettings() {
  const { enqueueSnackbar } = useSnackbar()
  const [configRow, setConfigRow] = useState(null)
  const [selectedWindows, setSelectedWindows] = useState(baseConfigValue.windows)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const safeParse = useCallback((value) => {
    if (!value) return {}
    if (typeof value === "object") return value
    try {
      return JSON.parse(value)
    } catch {
      return {}
    }
  }, [])

  const loadConfig = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await getSystemConfigByKey(CONFIG_KEY)
      verifyResponse(response)
      const row = response?.data || response
      if (row?.id) {
        setConfigRow(row)
        const parsed = safeParse(row?.configValueJson ?? row?.configValue)
        const windows = Array.isArray(parsed.windows)
          ? parsed.windows.filter((w) => BASE_WINDOWS.some((b) => b.id === w))
          : null
        setSelectedWindows(windows ?? baseConfigValue.windows)
      } else {
        setConfigRow(null)
        setSelectedWindows(baseConfigValue.windows)
      }
    } catch (err) {
      console.error("load periodic report config", err)
      setError("Unable to load periodic report settings.")
    } finally {
      setLoading(false)
    }
  }, [safeParse])

  useEffect(() => {
    loadConfig()
  }, [loadConfig])

  const handleWindowToggle = (windowId) => {
    setSelectedWindows((prev) =>
      prev.includes(windowId) ? prev.filter((w) => w !== windowId) : [...prev, windowId],
    )
  }

  const orderedWindows = useMemo(
    () => BASE_WINDOWS.filter((w) => selectedWindows.includes(w.id)),
    [selectedWindows],
  )

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    const payload = {
      windows: orderedWindows.map((w) => w.id),
    }
    const body = {
      name: "Periodic report settings",
      configKey: CONFIG_KEY,
      description:
        "Controls which periodic report windows run in the backend and which audience receives them.",
      configValue: JSON.stringify(payload),
    }
    try {
      let response
      if (configRow?.id) {
        response = await editSystemConfigs({ ...body, id: configRow.id })
      } else {
        response = await addSystemConfigs(body)
      }
      verifyResponse(response)
      enqueueSnackbar("Periodic report settings saved", { variant: "success" })
      await loadConfig()
    } catch (err) {
      console.error("save config", err)
      setError("Failed to save periodic report settings.")
    } finally {
      setSaving(false)
    }
  }

  const windowsSummary = orderedWindows.length
    ? orderedWindows.map((w) => w.label).join(", ")
    : "No windows enabled"

  return (
    <PageContent>
      <PeriodicReportSettingsWrapper>
        <PageHeading>
          <div>Periodic Reports</div>
          <div className="actions">
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || loading}
            >
              {saving ? "Saving…" : configRow ? "Update settings" : "Create settings"}
            </Button>
          </div>
        </PageHeading>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <div className="section">
          <Alert severity="info">
            Periodic report rollout is now controlled from <strong>Beta Features</strong>. This page only manages
            which report windows are active.
          </Alert>
        </div>

        <div className="section">
          <FormLabel>Active windows</FormLabel>
          <FormGroup row className="windows">
            {BASE_WINDOWS.map((window) => (
              <FormControlLabel
                key={window.id}
                className="window-label"
                control={
                  <Checkbox
                    checked={selectedWindows.includes(window.id)}
                    onChange={() => handleWindowToggle(window.id)}
                  />
                }
                label={window.label}
              />
            ))}
          </FormGroup>
          <div className="helper-text">
            Reports will only generate when the selected windows align with the completed period. The saved
            list is ordered from the shortest window to the longest.
          </div>
        </div>

        <div className="section">
          <FormLabel>Current windows</FormLabel>
          <p className="helper-text">{windowsSummary || "None configured"}</p>
        </div>
      </PeriodicReportSettingsWrapper>
      <LoadingScreen loading={loading} />
    </PageContent>
  )
}
